/**
 * Canvas helpers shared by the Print File Review editor (trim / crop / resize /
 * rotate / background / final composition). Pure client-side image math,
 * no network calls.
 */
import Pica from 'pica';

/**
 * Trim transparent margins. Pixels with alpha <= alphaThreshold count as
 * empty: the soft eraser and remove.bg alpha masks leave invisible residues
 * (alpha 1-8) that would otherwise pin the old bounding box, so an erased
 * region would stay in the print file as blank space.
 */
export function trimCanvas(canvas: HTMLCanvasElement, alphaThreshold = 8): void {
    const ctx = canvas.getContext('2d')!;
    const width = canvas.width;
    const height = canvas.height;
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    let top: number | null = null, bottom: number | null = null, left: number | null = null, right: number | null = null;

    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] > alphaThreshold) {
            const x = (i / 4) % width;
            const y = Math.floor((i / 4) / width);
            if (top === null) top = y;
            if (bottom === null) bottom = y;
            if (left === null) left = x;
            if (right === null) right = x;

            if (y < top!) top = y;
            if (y > bottom!) bottom = y;
            if (x < left!) left = x;
            if (x > right!) right = x;
        }
    }

    if (top === null) return;

    const padding = 1;
    top = Math.max(0, top - padding);
    bottom = Math.min(height - 1, bottom! + padding);
    left = Math.max(0, left! - padding);
    right = Math.min(width - 1, right! + padding);

    const trimmedWidth = right - left + 1;
    const trimmedHeight = bottom - top + 1;
    if (trimmedWidth === width && trimmedHeight === height) return;
    cropCanvasInPlace(canvas, left, top, trimmedWidth, trimmedHeight);
}

/**
 * Crop a canvas to a rect via drawImage. Unlike getImageData/putImageData this
 * never unpremultiplies alpha, so semi-transparent edge pixels keep their
 * exact colors (no rounding on every trim/crop).
 */
export function cropCanvasInPlace(canvas: HTMLCanvasElement, x: number, y: number, w: number, h: number): void {
    const temp = document.createElement('canvas');
    temp.width = w;
    temp.height = h;
    temp.getContext('2d')!.drawImage(canvas, x, y, w, h, 0, 0, w, h);
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d')!.drawImage(temp, 0, 0);
}

const pica = new Pica();

/**
 * Resize so the longest side equals targetSize inches at 72 DPI.
 * Resampling goes through pica (MKS2013 filter + unsharp mask) — the browser's
 * drawImage filter averages pixels and visibly softens fur/outline detail
 * compared to designer tools (Photoshop/Bridge use Lanczos + sharpen).
 * `sharpen=false` skips the unsharp pass (used for the editing master, so the
 * final output isn't sharpened twice).
 */
export async function resizeCanvas(
    canvas: HTMLCanvasElement,
    targetSizeCm: number,
    bgColor: string,
    sharpen = true,
): Promise<void> {
    const DPI = 72;
    const targetPixels = targetSizeCm * DPI;
    const width = canvas.width;
    const height = canvas.height;
    const maxSide = Math.max(width, height);
    const scale = targetPixels / maxSide;

    const newWidth = Math.round(width * scale);
    const newHeight = Math.round(height * scale);

    if (newWidth !== width || newHeight !== height) {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = newWidth;
        tempCanvas.height = newHeight;
        try {
            await pica.resize(canvas, tempCanvas, sharpen ? {
                unsharpAmount: 60,
                unsharpRadius: 0.6,
                unsharpThreshold: 2,
            } : {});
        } catch {
            // Fallback: stepped drawImage halving (softer, but never fails)
            let src: HTMLCanvasElement = canvas;
            let sw = width;
            let sh = height;
            while (sw * 0.5 > newWidth && sh * 0.5 > newHeight) {
                const half = document.createElement('canvas');
                half.width = Math.max(newWidth, Math.round(sw / 2));
                half.height = Math.max(newHeight, Math.round(sh / 2));
                const hctx = half.getContext('2d')!;
                hctx.imageSmoothingEnabled = true;
                hctx.imageSmoothingQuality = 'high';
                hctx.drawImage(src, 0, 0, sw, sh, 0, 0, half.width, half.height);
                src = half;
                sw = half.width;
                sh = half.height;
            }
            const tctx = tempCanvas.getContext('2d')!;
            tctx.imageSmoothingEnabled = true;
            tctx.imageSmoothingQuality = 'high';
            tctx.drawImage(src, 0, 0, sw, sh, 0, 0, newWidth, newHeight);
        }
        canvas.width = newWidth;
        canvas.height = newHeight;
        canvas.getContext('2d')!.drawImage(tempCanvas, 0, 0);
    }

    if (bgColor === 'black') {
        const borderSize = 108;
        const borderedWidth = newWidth + (borderSize * 2);
        const borderedHeight = newHeight + (borderSize * 2);

        const borderedCanvas = document.createElement('canvas');
        borderedCanvas.width = borderedWidth;
        borderedCanvas.height = borderedHeight;
        const borderedCtx = borderedCanvas.getContext('2d')!;

        borderedCtx.fillStyle = 'black';
        borderedCtx.fillRect(0, 0, borderedWidth, borderedHeight);
        borderedCtx.drawImage(canvas, borderSize, borderSize);

        canvas.width = borderedWidth;
        canvas.height = borderedHeight;
        const finalCtx = canvas.getContext('2d')!;
        finalCtx.drawImage(borderedCanvas, 0, 0);
    }
}

/**
 * Trim uniform background-colored margins from an OPAQUE canvas (white/black JPG print files).
 * Needed after rotating a JPG: the rotation adds background-colored corners/margins
 * that trimCanvas() cannot detect because there is no alpha channel.
 */
export function trimCanvasByColor(canvas: HTMLCanvasElement, color: string, tolerance = 16, padding = 2): void {
    const ctx = canvas.getContext('2d')!;
    const width = canvas.width;
    const height = canvas.height;
    const data = ctx.getImageData(0, 0, width, height).data;
    const [br, bg, bb] = color === 'black' ? [0, 0, 0] : [255, 255, 255];

    let top = -1, bottom = -1, left = width, right = -1;
    for (let y = 0; y < height; y++) {
        let rowHasContent = false;
        const rowOff = y * width * 4;
        for (let x = 0; x < width; x++) {
            const i = rowOff + x * 4;
            if (
                Math.abs(data[i] - br) > tolerance ||
                Math.abs(data[i + 1] - bg) > tolerance ||
                Math.abs(data[i + 2] - bb) > tolerance
            ) {
                rowHasContent = true;
                if (x < left) left = x;
                if (x > right) right = x;
            }
        }
        if (rowHasContent) {
            if (top === -1) top = y;
            bottom = y;
        }
    }

    if (top === -1) return; // fully background — nothing to trim to

    top = Math.max(0, top - padding);
    bottom = Math.min(height - 1, bottom + padding);
    left = Math.max(0, left - padding);
    right = Math.min(width - 1, right + padding);

    const w = right - left + 1;
    const h = bottom - top + 1;
    if (w === width && h === height) return;

    cropCanvasInPlace(canvas, left, top, w, h);
}

export function rotateCanvas(canvas: HTMLCanvasElement, degrees: number): void {
    const image = canvas;
    const width = image.width;
    const height = image.height;
    const radians = degrees * Math.PI / 180;

    const sin = Math.abs(Math.sin(radians));
    const cos = Math.abs(Math.cos(radians));
    const newWidth = Math.floor(width * cos + height * sin);
    const newHeight = Math.floor(width * sin + height * cos);

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = newWidth;
    tempCanvas.height = newHeight;
    const tempCtx = tempCanvas.getContext('2d')!;
    tempCtx.imageSmoothingEnabled = true;
    tempCtx.imageSmoothingQuality = 'high';

    tempCtx.translate(newWidth / 2, newHeight / 2);
    tempCtx.rotate(radians);
    tempCtx.drawImage(image, -width / 2, -height / 2);

    canvas.width = newWidth;
    canvas.height = newHeight;
    const newCtx = canvas.getContext('2d')!;
    newCtx.imageSmoothingEnabled = true;
    newCtx.imageSmoothingQuality = 'high';
    newCtx.drawImage(tempCanvas, 0, 0);
}

export function applyBackgroundColor(canvas: HTMLCanvasElement, color: string): void {
    const ctx = canvas.getContext('2d')!;
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = 'source-over';
}

export function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Image load failed'));
        img.src = src;
    });
}

interface Point { x: number; y: number; }

function getContentPoints(ctx: CanvasRenderingContext2D, width: number, height: number): Point[] {
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    const points: Point[] = [];
    const step = 2;

    for (let y = 0; y < height; y += step) {
        for (let x = 0; x < width; x += step) {
            const alpha = data[(y * width + x) * 4 + 3];
            if (alpha > 10) {
                points.push({ x, y });
            }
        }
    }
    return points;
}

function getConvexHull(points: Point[]): Point[] {
    points.sort((a, b) => a.x - b.x || a.y - b.y);
    const cross = (o: Point, a: Point, b: Point) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

    const lower: Point[] = [];
    for (const p of points) {
        while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
            lower.pop();
        }
        lower.push(p);
    }

    const upper: Point[] = [];
    for (let i = points.length - 1; i >= 0; i--) {
        const p = points[i];
        while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
            upper.pop();
        }
        upper.push(p);
    }

    upper.pop();
    lower.pop();
    return lower.concat(upper);
}

/** Bounding box area of the points after rotating them by angleRad. */
function getBoundingBoxArea(points: Point[], angleRad: number): number {
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;

    for (const p of points) {
        const rx = p.x * cos - p.y * sin;
        const ry = p.x * sin + p.y * cos;
        if (rx < minX) minX = rx;
        if (rx > maxX) maxX = rx;
        if (ry < minY) minY = ry;
        if (ry > maxY) maxY = ry;
    }

    return (maxX - minX) * (maxY - minY);
}

/**
 * Rotation angle (degrees) that gives the minimum-area bounding box of the
 * opaque content, found with the Rotating Calipers method over the convex hull.
 * Returns the raw optimal angle; apply calculateOrientationCorrection() afterwards
 * to keep the object's original vertical/horizontal orientation.
 */
export function calculateMinAreaAngle(originalCanvas: HTMLCanvasElement): number {
    const maxDim = 500;
    const scale = Math.min(1, maxDim / Math.max(originalCanvas.width, originalCanvas.height));
    const w = Math.floor(originalCanvas.width * scale);
    const h = Math.floor(originalCanvas.height * scale);

    const smallCanvas = document.createElement('canvas');
    smallCanvas.width = w;
    smallCanvas.height = h;
    const ctx = smallCanvas.getContext('2d')!;
    ctx.drawImage(originalCanvas, 0, 0, w, h);

    const points = getContentPoints(ctx, w, h);
    if (points.length < 3) return 0;

    const hull = getConvexHull(points);

    let minArea = Infinity;
    let bestAngleRad = 0;

    // The minimum bounding box always has a side parallel to one hull edge.
    for (let i = 0; i < hull.length; i++) {
        const p1 = hull[i];
        const p2 = hull[(i + 1) % hull.length];
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const edgeAngle = Math.atan2(dy, dx);

        // Check both orientations: along the edge and perpendicular to it.
        const angle1 = -edgeAngle;
        const area1 = getBoundingBoxArea(hull, angle1);
        if (area1 < minArea) {
            minArea = area1;
            bestAngleRad = angle1;
        }

        const angle2 = -edgeAngle + (Math.PI / 2);
        const area2 = getBoundingBoxArea(hull, angle2);
        if (area2 < minArea) {
            minArea = area2;
            bestAngleRad = angle2;
        }
    }

    // Normalise to [-180, 180]
    let angleDeg = bestAngleRad * 180 / Math.PI;
    while (angleDeg > 180) angleDeg -= 360;
    while (angleDeg < -180) angleDeg += 360;

    return angleDeg;
}

/**
 * Correction (multiple of 90°) that undoes the orientation component of an
 * applied rotation, so an object rotated ~90° for alignment is turned back to
 * roughly its original upright/sideways orientation while staying aligned.
 */
export function calculateOrientationCorrection(appliedAngle: number): number {
    let normalized = appliedAngle % 360;
    if (normalized < 0) normalized += 360;

    if (normalized >= 315 || normalized < 45) return 0;
    if (normalized >= 45 && normalized < 135) return -90;
    if (normalized >= 135 && normalized < 225) return -180;
    return 90;
}

function cloneCanvas(src: HTMLCanvasElement): HTMLCanvasElement {
    const c = document.createElement('canvas');
    c.width = src.width;
    c.height = src.height;
    c.getContext('2d')!.drawImage(src, 0, 0);
    return c;
}

/**
 * Compose the final print file from the TRANSPARENT resized object:
 * black gets the 108px bleed border, white/black get the background fill,
 * transparent stays PNG. The source canvas is not modified.
 * `borderSize` overrides the black bleed (e.g. to match a server draft without one).
 */
export async function composeFinalBlob(
    canvas: HTMLCanvasElement,
    bgColor: string,
    borderSize: number = bgColor === 'black' ? 108 : 0,
): Promise<{ blob: Blob; width: number; height: number }> {
    let out = cloneCanvas(canvas);
    if (bgColor === 'black' && borderSize > 0) {
        const bordered = document.createElement('canvas');
        bordered.width = out.width + borderSize * 2;
        bordered.height = out.height + borderSize * 2;
        const bctx = bordered.getContext('2d')!;
        bctx.drawImage(out, borderSize, borderSize);
        out = bordered;
    }
    if (bgColor && bgColor !== 'transparent') {
        applyBackgroundColor(out, bgColor);
    }
    const format = bgColor && bgColor !== 'transparent' ? 'image/jpeg' : 'image/png';
    const blob = await new Promise<Blob | null>((resolve) => out.toBlob((b) => resolve(b), format, 1));
    if (!blob) throw new Error('Failed to compose final blob');
    return { blob, width: out.width, height: out.height };
}
